/**
 * Avatar for a given user. If the user has no avatar, we use a fallback.
 *
 * This component also supports the optional display of a profile preview hover
 * card (discussion-hover-card.jsx).
 */
import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import gql from "graphql-tag";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {color} from "@khanacademy/wonder-blocks-tokens";
import Tooltip, {TooltipContent} from "@khanacademy/wonder-blocks-tooltip";
import {gqlOp, type GqlOperation} from "@repo/data-gql-core";
import {staticUrl} from "@repo/urls";

// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import DeprecatedLink from "#components/link-package/deprecated-link.ts";
import ApolloQuery from "#data-access/gql/apollo-query.tsx";
import withUserProfile, {
    type WithoutUserProfile,
    type WithUserProfileProps,
} from "#data-access/with-user-profile.tsx";

import * as sharedStyles from "../shared-styles.ts";
import {getAriaAvatarName} from "../util.tsx";

import ApolloWrappedDiscussionHoverCard from "./discussion-hover-card.tsx";

type DefaultProps = {
    showCurrentUserAvatar: boolean;
    // The following four props are specific to not showCurrentUserAvatar uses of this component.
    avatarName: string | null | undefined;
    avatarSrc: string;
    username: string;
    kaid: string;
    enableProfilePreviewTooltip: boolean;
};

type Props = DefaultProps & WithUserProfileProps;

export const DATA_QUERY: GqlOperation<Data> = gqlOp(gql`
    query discussionAvatar {
        user {
            id
            avatar {
                name
                imageSrc
            }
        }
    }
`);

type Data = {
    user?: {
        id: string;
        avatar: {
            name: string;
            imageSrc: string;
        };
    };
};

class DiscussionAvatar extends React.Component<Props> {
    static defaultProps: DefaultProps = {
        showCurrentUserAvatar: false,
        avatarName: "Default Khan Academy avatar",
        avatarSrc: "/images/avatars/svg/blobby-green.svg",
        username: "",
        kaid: "",
        enableProfilePreviewTooltip: false,
    };

    renderImage: (
        username: string,
        avatarName: string | null | undefined,
        avatarSrc: string,
    ) => React.ReactNode = (
        username: string,
        avatarName: string | null | undefined,
        avatarSrc: string,
    ) => {
        let avatarImage = null;

        if (!avatarName) {
            // @ts-expect-error - TS2322 - Type 'Element' is not assignable to type 'null'.
            avatarImage = (
                <img
                    className={css(styles.image)}
                    src={staticUrl(avatarSrc)}
                    alt=""
                    aria-hidden={true}
                />
            );
        } else {
            // @ts-expect-error - TS2322 - Type 'Element' is not assignable to type 'null'.
            avatarImage = (
                <img
                    className={css(styles.image)}
                    src={staticUrl(avatarSrc)}
                    alt={t`${getAriaAvatarName(avatarName)} avatar for user ${username}`}
                />
            );
        }

        return <View style={styles.container}>{avatarImage}</View>;
    };

    renderTooltip: (
        username: string,
        avatarName: string | null | undefined,
        kaid: string,
        avatarImgComponent: React.ReactNode,
    ) => React.ReactNode = (
        username: string,
        avatarName: string | null | undefined,
        kaid: string,
        avatarImgComponent: React.ReactNode,
    ) => {
        const tooltipContent: React.ReactNode = (
            <ApolloWrappedDiscussionHoverCard kaid={kaid} />
        );
        const ariaLabel = `${t`${getAriaAvatarName(avatarName)} avatar for user ${username}`} ${t`Go to ${username}'s user profile.`}`;
        const profileUrl = `/profile/${kaid}/discussion`;

        return (
            <Tooltip
                // NOTE(jeremy): The children of TooltipContent are required to
                // be text nodes (strings or Typography components), but in
                // reality, the rule is closer to: the content should not be
                // interactive as that breaks accessibility rules. I've
                // verified that in this instance, the content we're displaying
                // here is not interactive so I'm FlowIgnoring this for today.
                content={<TooltipContent>{tooltipContent}</TooltipContent>}
            >
                <DeprecatedLink href={profileUrl} aria-label={ariaLabel}>
                    {avatarImgComponent}
                </DeprecatedLink>
            </Tooltip>
        );
    };

    render(): React.ReactNode {
        const {
            showCurrentUserAvatar,
            avatarSrc,
            avatarName,
            username,
            enableProfilePreviewTooltip,
            kaid,
            profile,
        } = this.props;

        let avatarImgComponent;

        if (!showCurrentUserAvatar || !profile) {
            avatarImgComponent = this.renderImage(
                username,
                avatarName,
                avatarSrc,
            );
            return enableProfilePreviewTooltip
                ? this.renderTooltip(
                      username,
                      avatarName,
                      kaid,
                      avatarImgComponent,
                  )
                : avatarImgComponent;
        }

        return (
            <ApolloQuery query={DATA_QUERY} fetchPolicy="cache-and-network">
                {({data}) => {
                    // When loading, use the default values; when done switch
                    // in the values for this user.
                    const usernameForImage = profile.get("username");
                    const avatarNameForImage =
                        data?.user?.avatar.name || avatarName;
                    const avatarSrcForImage =
                        data?.user?.avatar.imageSrc || avatarSrc;
                    avatarImgComponent = this.renderImage(
                        usernameForImage,
                        avatarNameForImage,
                        avatarSrcForImage,
                    );
                    return enableProfilePreviewTooltip
                        ? this.renderTooltip(
                              username,
                              avatarName,
                              kaid,
                              avatarImgComponent,
                          )
                        : avatarImgComponent;
                }}
            </ApolloQuery>
        );
    }
}

const containerSize = 48;
const imageSize = 32;

const styles = StyleSheet.create({
    container: {
        borderRadius: "50%",
        width: containerSize,
        minWidth: containerSize,
        height: containerSize,
        border: `solid 2px ${sharedStyles.borderColor}`,
        backgroundColor: color.white,
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
    },
    image: {
        width: imageSize,
        height: imageSize,
    },
});

type ExportProps = WithoutUserProfile<
    JSX.LibraryManagedAttributes<
        typeof DiscussionAvatar,
        React.ComponentProps<typeof DiscussionAvatar>
    >
>;

export default withUserProfile(
    DiscussionAvatar,
) as React.ComponentType<ExportProps>;
